import axios from 'axios';

const state = {
  bulkData: null,
  isLoading: false,
  error: null,
};

export const getters = {
  getBulkData: $state => $state.bulkData,
  isLoading: $state => $state.isLoading,
  getError: $state => $state.error,
};

export const actions = {
  fetchBulkData: async (
    { commit, state: $state },
    { since, until: untilDate, headers, pageSize = 10000 }
  ) => {
    // Prevent duplicate requests
    if ($state.isLoading) return null;

    commit('SET_LOADING', true);
    commit('SET_ERROR', null);

    try {
      let currentOffset = 0;
      let hasMore = true;
      let accumulatedSummary = null;
      const accumulatedDailyBreakdown = {};
      const accumulatedConversationMetrics = [];
      let totalConversations = 0;
      let tokenLimit = null;
      let tokenPeriodStart = null;
      let tokenPeriodEnd = null;
      let tokenCycleAnchorDate = null;

      while (hasMore) {
        const params = {
          since,
          until: untilDate,
          limit: pageSize,
          offset: currentOffset,
        };

        const response = await axios.get('/api/v1/interactions/bulk_data', {
          headers,
          params,
        });

        const pageData = response.data;
        const pagination = pageData.pagination || {};

        if (tokenLimit === null && pageData.token_limit !== undefined) {
          tokenLimit = pageData.token_limit;
        }
        if (tokenPeriodStart === null && pageData.token_period_start) {
          tokenPeriodStart = pageData.token_period_start;
        }
        if (tokenPeriodEnd === null && pageData.token_period_end) {
          tokenPeriodEnd = pageData.token_period_end;
        }
        if (tokenCycleAnchorDate === null && pageData.token_cycle_anchor_date) {
          tokenCycleAnchorDate = pageData.token_cycle_anchor_date;
        }

        if (pageData.bulk_data) {
          totalConversations = pagination.total_available || 0;

          // Accumulate summary
          if (pageData.bulk_data.summary) {
            if (!accumulatedSummary) {
              accumulatedSummary = { ...pageData.bulk_data.summary };
            } else {
              accumulatedSummary.billable_interactions =
                (accumulatedSummary.billable_interactions || 0) +
                (pageData.bulk_data.summary.billable_interactions || 0);
              accumulatedSummary.total_interactions =
                (accumulatedSummary.total_interactions || 0) +
                (pageData.bulk_data.summary.total_interactions || 0);
              accumulatedSummary.finished_interactions =
                (accumulatedSummary.finished_interactions || 0) +
                (pageData.bulk_data.summary.finished_interactions || 0);
              accumulatedSummary.unfinished_interactions =
                (accumulatedSummary.unfinished_interactions || 0) +
                (pageData.bulk_data.summary.unfinished_interactions || 0);
              accumulatedSummary.conversations_with_interactions =
                (accumulatedSummary.conversations_with_interactions || 0) +
                (pageData.bulk_data.summary.conversations_with_interactions ||
                  0);
              if (pageData.bulk_data.summary.unfinished_conversation_ids) {
                accumulatedSummary.unfinished_conversation_ids = [
                  ...(accumulatedSummary.unfinished_conversation_ids || []),
                  ...pageData.bulk_data.summary.unfinished_conversation_ids,
                ];
              }
            }
          }

          // Accumulate daily breakdown (merge by date)
          if (pageData.bulk_data.daily_breakdown) {
            pageData.bulk_data.daily_breakdown.forEach(day => {
              if (!accumulatedDailyBreakdown[day.date]) {
                accumulatedDailyBreakdown[day.date] = { ...day };
              } else {
                accumulatedDailyBreakdown[day.date].total_interactions +=
                  day.total_interactions || 0;
                accumulatedDailyBreakdown[day.date].total_ai_interactions +=
                  day.total_ai_interactions || 0;
                accumulatedDailyBreakdown[day.date].unique_conversations +=
                  day.unique_conversations || 0;
              }
            });
          }

          // Accumulate conversation metrics
          if (pageData.bulk_data.conversation_metrics) {
            accumulatedConversationMetrics.push(
              ...pageData.bulk_data.conversation_metrics
            );
          }
        }

        hasMore = pagination.has_more || false;
        currentOffset += pageSize;

        if (currentOffset >= 100000) {
          hasMore = false;
        }
      }

      const dailyBreakdownArray = Object.values(accumulatedDailyBreakdown).sort(
        (a, b) => a.date.localeCompare(b.date)
      );

      const finalData = {
        token_limit: tokenLimit,
        token_period_start: tokenPeriodStart,
        token_period_end: tokenPeriodEnd,
        token_cycle_anchor_date: tokenCycleAnchorDate,
        bulk_data: {
          summary: accumulatedSummary,
          daily_breakdown: dailyBreakdownArray,
          conversation_metrics: accumulatedConversationMetrics,
          total_conversations: totalConversations,
        },
        pagination: {
          total_returned: accumulatedConversationMetrics.length,
          total_available: totalConversations,
          has_more: false,
        },
      };

      commit('SET_BULK_DATA', finalData);

      return finalData;
    } catch (err) {
      commit('SET_ERROR', err);
      return null;
    } finally {
      commit('SET_LOADING', false);
    }
  },
};

export const mutations = {
  SET_BULK_DATA: ($state, data) => {
    $state.bulkData = data;
  },
  SET_LOADING: ($state, value) => {
    $state.isLoading = value;
  },
  SET_ERROR: ($state, error) => {
    $state.error = error;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
